import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { adminClient, type AdminIntegrationVisibilityRow } from '@/api/adminClient';
import { AdminSection } from '@/components/shell/AdminSection';
import { SearchInput } from '@/components/table/SearchInput';
import { Button } from '@/components/ui/button';
import { LoadError, SkeletonList } from '@/components/ui/load-state';

const ALL = '*';

const sameSet = (a: ReadonlySet<string>, b: ReadonlySet<string>) => a.size === b.size && [...a].every((id) => b.has(id));

/**
 * The integrations a person may use from an external harness: `studio ops` in the CLI and the
 * Studio MCP in Claude. Nothing checked is nothing; "All integrations" is one grant for every one.
 */
export function PersonExternalAccess({ userId, personName }: { userId: string; personName: string }) {
  const [integrations, setIntegrations] = useState<AdminIntegrationVisibilityRow[] | undefined>();
  const [saved, setSaved] = useState<ReadonlySet<string> | undefined>();
  const [checked, setChecked] = useState<ReadonlySet<string>>(new Set());
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setError(undefined);
    try {
      const [grants, catalog] = await Promise.all([adminClient.getPersonExternalAccess(userId), adminClient.listIntegrationVisibility()]);
      const granted = new Set(grants.integrationIds);
      setSaved(granted);
      setChecked(granted);
      setIntegrations([...catalog.items].sort((a, b) => a.title.localeCompare(b.title)));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Could not load external access');
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (integrations ?? []).filter((row) => !needle || row.title.toLowerCase().includes(needle) || row.integrationId.toLowerCase().includes(needle));
  }, [integrations, query]);

  const toggle = (id: string, on: boolean) =>
    setChecked((current) => {
      const next = new Set(current);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const save = async () => {
    setSaving(true);
    try {
      const result = await adminClient.setPersonExternalAccess(userId, [...checked]);
      const granted = new Set(result.integrationIds);
      setSaved(granted);
      setChecked(granted);
      toast.success(`Saved ${personName}’s external access`);
    } catch (saveError) {
      toast.error(saveError instanceof Error ? saveError.message : 'Could not save external access');
    } finally {
      setSaving(false);
    }
  };

  const all = checked.has(ALL);
  const changed = saved !== undefined && !sameSet(saved, checked);

  return (
    <AdminSection
      title="External access"
      description="The integrations this person may use from Claude, Codex or the Studio CLI (studio ops and the Studio MCP). With nothing checked, they can use none."
      actions={
        <Button type="button" size="sm" onClick={() => void save()} disabled={!changed || saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      }
    >
      {error ? (
        <LoadError message={error} onRetry={() => void load()} />
      ) : !integrations || !saved ? (
        <SkeletonList rows={6} className="space-y-3 p-4" />
      ) : (
        <div className="space-y-3 p-4">
          <label className="flex cursor-pointer items-center gap-3 rounded-md border border-border p-3 text-sm font-medium">
            <input type="checkbox" checked={all} onChange={(event) => toggle(ALL, event.target.checked)} />
            All integrations, also the ones added later
          </label>
          <SearchInput value={query} onChange={setQuery} placeholder="Filter integrations" label="Filter integrations" />
          <ul className="grid gap-1 sm:grid-cols-2">
            {shown.map((row) => (
              <li key={row.integrationId}>
                <label className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm hover:bg-muted/50">
                  <input
                    type="checkbox"
                    checked={all || checked.has(row.integrationId)}
                    disabled={all}
                    onChange={(event) => toggle(row.integrationId, event.target.checked)}
                  />
                  <span className="min-w-0 truncate">{row.title}</span>
                  <span className="ml-auto shrink-0 font-mono text-xs text-muted-foreground">{row.integrationId}</span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
    </AdminSection>
  );
}
